import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import SessionManager from "./SessionManager";
import jwtDecode from "jwt-decode";

/**
 * This class provides in-app authentication using amazon-cognito-identity-js
 * instead of redirecting to Cognito's hosted UI. This solves browser compatibility
 * issues with crypto APIs.
 */
export class CognitoIdentityService {
  #userPool = null;
  #refreshListener = null;
  #refreshTimer = null;
  #session = null;
  #config = null;

  constructor(apiConfig = {}) {
    console.debug("CognitoIdentityService: initialise");
    this.#session = new SessionManager();
    this.#config = { ...apiConfig };

    // Initialize the Cognito User Pool
    this.#userPool = new CognitoUserPool({
      UserPoolId: this.#config.userPoolId,
      ClientId: this.#config.clientId,
    });

    this.#startRefreshTimer();
  }

  /**
   * Sign in a user with username and password
   *
   * @param {string} username - The username (email)
   * @param {string} password - The password
   * @returns {Promise<object>} - The session data
   */
  async signIn(username, password) {
    return new Promise((resolve, reject) => {
      try {
        const authenticationDetails = new AuthenticationDetails({
          Username: username,
          Password: password,
        });

        const userData = {
          Username: username,
          Pool: this.#userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            const idToken = result.getIdToken().getJwtToken();
            const accessToken = result.getAccessToken().getJwtToken();
            const refreshToken = result.getRefreshToken().getToken();

            // Format the token data to match the existing structure
            const tokenData = {
              id_token: idToken,
              access_token: accessToken,
              refresh_token: refreshToken,
              expires_in: 3600, // Default expiry time in seconds
              token_type: "Bearer",
            };

            // Add expiry time
            tokenData.expires_at = this.#calculateTokenExpiry(tokenData.expires_in);

            // Store the session
            this.#session.storeSession(tokenData);

            // Start refresh timer
            this.#startRefreshTimer();

            // Trigger session listener
            this.#triggerSessionListener();

            resolve(tokenData);
          },
          onFailure: (err) => {
            if ("UserNotConfirmedException" === err.code) {
              // handles unconfirmed user by resending code
              cognitoUser.resendConfirmationCode((confirmError) => {
                if (confirmError) {
                  reject(confirmError);
                  return;
                }
                reject("USER_NOT_CONFIRMED");
              });
            } else {
              console.error("Authentication failed:", err.code);
              reject(err);
            }
          },
        });
      } catch (error) {
        console.error("Error during sign in:", error);
        reject(error);
      }
    });
  }

  /**
   * Sign up a new user
   *
   * @param {string} username - The username (email)
   * @param {string} password - The password
   * @param {object} attributes - Additional user attributes
   * @returns {Promise<object>} - The signup result
   */
  async register(username, password, attributes = {}) {
    return new Promise((resolve, reject) => {
      try {
        const attributeList = [];

        // Convert attributes to CognitoUserAttributes
        for (const key in attributes) {
          const attribute = new CognitoUserAttribute({
            Name: key,
            Value: attributes[key],
          });
          attributeList.push(attribute);
        }

        this.#userPool.signUp(username, password, attributeList, null, (err, result) => {
          if (err) {
            console.error("Error during sign up:", err);
            reject(err);
            return;
          }

          resolve(result);
        });
      } catch (error) {
        console.error("Error during sign up:", error);
        reject(error);
      }
    });
  }

  /**
   * Confirm a user's registration with verification code
   *
   * @param {string} username - The username (email)
   * @param {string} code - The verification code
   * @returns {Promise<object>} - The confirmation result
   */
  async confirmRegistration(username, code) {
    return new Promise((resolve, reject) => {
      try {
        const userData = {
          Username: username,
          Pool: this.#userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            console.log("code expired:", err);
            if ("NotAuthorizedException" === err.code && err.message.match(/status is CONFIRMED/i)) {
              reject("USER_ALREADY_CONFIRMED");
              return;
            }
            if ("ExpiredCodeException" === err.code) {
              reject("CONFIRMATION_EXPIRED");
              return;
            }
            console.error("Error during confirmation:", err);
            reject(err);
            return;
          }

          resolve(result);
        });
      } catch (error) {
        console.error("Error during confirmation:", error);
        reject(error);
      }
    });
  }

  async resendConfirmationCode(username) {
    return new Promise((resolve, reject) => {
      try {
        const userData = {
          Username: username,
          Pool: this.#userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            console.error("Error during resend confirmation code:", err);
            reject(err);
            return;
          }

          resolve(result);
        });
      } catch (error) {
        console.error("Error during resend confirmation code:", error);
        reject(error);
      }
    });
  }

  async forgotPassword(email) {
    return new Promise((resolve, reject) => {
      try {
        const username = String(email ?? "").trim();
        const userData = {
          Username: username,
          Pool: this.#userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.forgotPassword({
          onSuccess: (result) => {
            resolve(result);
          },
          onFailure: (err) => {
            console.error("Error during forgot password:", err);
            reject(err);
          },
        });
      } catch (error) {
        console.error("Error during forgot password:", error);
        reject(error);
      }
    });
  }

  /**
   * Confirm new password with verification code
   *
   * @param {string} username - The username (email)
   * @param {string} code - The verification code
   * @param {string} newPassword - The new password
   * @returns {Promise<object>} - The result
   */
  async confirmPassword(username, code, newPassword) {
    return new Promise((resolve, reject) => {
      try {
        const userData = {
          Username: username,
          Pool: this.#userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve({ success: true });
          },
          onFailure: (err) => {
            console.error("Error during confirm password:", err);
            reject(err);
          },
        });
      } catch (error) {
        console.error("Error during confirm password:", error);
        reject(error);
      }
    });
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    try {
      const currentUser = this.#userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }

      this.#session.clearSession();
      this.#triggerSessionListener();
      return true;
    } catch (error) {
      console.error("Error during sign out:", error);
      throw error;
    }
  }

  /**
   * Refresh the current session token
   *
   * @returns {Promise<object>} - The refreshed token data
   */
  async refreshAuthorizationToken() {
    return new Promise((resolve) => {
      try {
        console.debug("CognitoIdentityService: refresh");
        const currentUser = this.#userPool.getCurrentUser();
        if (!currentUser) {
          this.#session.clearSession();
          resolve({ success: false, error: "No current user found" });
          return;
        }

        // get auth tokens
        const { authTokens } = this.#session.getSessionData();
        const refreshToken = new CognitoRefreshToken({ RefreshToken: authTokens.refresh_token });

        // call refresh session
        currentUser.refreshSession(refreshToken, (err, session) => {
          if (err) {
            console.warn(`CognitoIdentityService: refresh-session error ${err}`);
            this.#session.clearSession();
            resolve({ success: false, error: "Error refreshing token" });
            return;
          }

          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();

          // Format the token data to match the existing structure
          const tokenData = {
            id_token: idToken,
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_in: 3600, // Default expiry time in seconds
            token_type: "Bearer",
          };

          // Add expiry time
          tokenData.expires_at = this.#calculateTokenExpiry(tokenData.expires_in);

          // Store the session
          this.#session.storeSession(tokenData);

          resolve({ success: true, data: tokenData });
        });
      } catch (error) {
        console.error("CognitoIdentityService: unhandled-error", error);
        this.#session.clearSession();
        resolve({ success: false, error: "Unhandled error refreshing token" });
      }
    }).finally(() => this.#triggerSessionListener());
  }

  /**
   * Gets the stored session data and converts it for use by our application
   */
  getSession() {
    const { authTokens, preAuthUri } = this.#session.getSessionData();

    // check token exists and is valid (not expired)
    const now = new Date().getTime();
    const { id_token, expires_at } = authTokens;
    const authenticated = id_token ? now < expires_at : false;

    if (authenticated) {
      const userInfo = jwtDecode(id_token);
      return {
        userInfo,
        authenticated,
        token: id_token,
        redirectUri: preAuthUri,
      };
    } else {
      return {
        token: null,
        userInfo: {},
        authenticated,
        redirectUri: preAuthUri,
      };
    }
  }

  /**
   * Adds callback for session updates (token refresh)
   *
   * @param listener
   */
  setSessionListener(listener) {
    this.#refreshListener = listener;
  }

  /**
   * Clear refresh timers and remove listener
   */
  cleanup() {
    this.setSessionListener(null);
    this.#disarmRefreshTimer();
  }

  /**
   * If we have a session listener, we trigger it when we update token states
   */
  #triggerSessionListener() {
    // fire session listener to update state across app
    if (typeof this.#refreshListener === "function") {
      const session = this.getSession();
      this.#refreshListener(session);
    }
  }

  /**
   * Used to automatically refresh cognito tokens
   */
  #startRefreshTimer() {
    if (this.#config.autoRefresh) {
      const { authTokens } = this.#session.getSessionData();
      const { refresh_token, expires_at } = authTokens;
      if (refresh_token && expires_at) {
        const now = new Date().getTime();
        const timeToExpire = expires_at - now;
        this.#armRefreshTimer(timeToExpire);
      }
    }
  }

  /**
   * Process that will refresh tokens and reschedule the next call
   * @param duration
   */
  #armRefreshTimer(duration) {
    this.#disarmRefreshTimer();
    this.#refreshTimer = window.setTimeout(async () => {
      try {
        const result = await this.refreshAuthorizationToken();
        if (result.success) {
          // restart timer after refresh
          this.#startRefreshTimer();
        }
      } catch (e) {
        console.error(`CognitoIdentityService: auto-refresh fail-${this.#refreshTimer}: ${e}`);
      }
    }, duration);
    console.debug(
      `CognitoIdentityService: auto-refresh arm-${this.#refreshTimer}:`,
      duration > 0 ? Math.trunc(duration / 1000 / 60) : "now",
    );
  }

  #disarmRefreshTimer() {
    if (this.#refreshTimer) {
      console.debug(`CognitoIdentityService: auto-refresh disarm-${this.#refreshTimer}`);
      clearTimeout(this.#refreshTimer);
    }
  }

  /**
   * Calculates expiry for token used for automatic refresh process
   *
   * @param expiresIn token expiry from cognito
   * @returns {number}
   */
  #calculateTokenExpiry(expiresIn) {
    if (typeof expiresIn === "number") {
      const { refreshSlack = 60 } = this.#config;
      const now = new Date().getTime();
      return now + (expiresIn - refreshSlack) * 1000;
    }
    return -1;
  }
}
